// (c) chaehongee. Confidential and proprietary.

import { createSlice, createAsyncThunk, type PayloadAction } from '@reduxjs/toolkit'
import type { SavedNodesBySessionResponse } from 'models-ts/saved-node'
import {
    saveNode as apiSaveNode,
    unsaveNode as apiUnsaveNode,
    listSavedNodes as apiListSavedNodes,
    checkSavedNodes as apiCheckSavedNodes,
} from '../../services/savedNode'

interface SavedNodesState {
    /** Set of node IDs the current user has saved (for canvas badge rendering). */
    savedNodeIds: Record<string, boolean>
    /** Full saved nodes grouped by session (for Saved backstage tab). */
    sessionGroups: SavedNodesBySessionResponse[]
    /** Whether the full list has been fetched. */
    listLoaded: boolean
    loading: boolean
}

const initialState: SavedNodesState = {
    savedNodeIds: {},
    sessionGroups: [],
    listLoaded: false,
    loading: false,
}

/** Batch check which nodes are saved (called on session load). */
export const checkSavedNodes = createAsyncThunk(
    'savedNodes/check',
    async (nodeIds: string[]) => {
        const res = await apiCheckSavedNodes(nodeIds)
        return res.saved_node_ids
    },
)

/** Fetch full saved nodes list grouped by session (for backstage tab). */
export const fetchSavedNodes = createAsyncThunk(
    'savedNodes/fetchAll',
    async () => {
        const res = await apiListSavedNodes()
        return res
    },
    {
        condition: (_, { getState }) => {
            const state = (getState() as { savedNodes: SavedNodesState }).savedNodes
            return !state.loading
        },
    },
)

/** Save a node (optimistic). */
export const saveNode = createAsyncThunk(
    'savedNodes/save',
    async (nodeId: string) => {
        const res = await apiSaveNode(nodeId)
        return res
    },
)

/** Unsave a node (optimistic). */
export const unsaveNode = createAsyncThunk(
    'savedNodes/unsave',
    async (nodeId: string) => {
        await apiUnsaveNode(nodeId)
        return nodeId
    },
)

const savedNodesSlice = createSlice({
    name: 'savedNodes',
    initialState,
    reducers: {
        /** Remove a node ID from savedNodeIds (e.g., when a node is deleted). */
        removeSavedNodeId: (state, action: PayloadAction<string>) => {
            delete state.savedNodeIds[action.payload]
        },
        /** Update a session title within the saved groups. */
        updateSavedSessionTitle: (
            state,
            action: PayloadAction<{ sessionId: string; title: string }>
        ) => {
            const group = state.sessionGroups.find(
                (g) => g.session_id === action.payload.sessionId
            )
            if (group) {
                group.session_title = action.payload.title
            }
        },
    },
    extraReducers: (builder) => {
        builder
            // checkSavedNodes
            .addCase(checkSavedNodes.fulfilled, (state, action) => {
                for (const id of action.payload) {
                    state.savedNodeIds[id] = true
                }
            })
            // fetchSavedNodes
            .addCase(fetchSavedNodes.pending, (state) => {
                state.loading = true
            })
            .addCase(fetchSavedNodes.fulfilled, (state, action) => {
                state.sessionGroups = action.payload.sessions
                state.listLoaded = true
                state.loading = false
                // Also rebuild savedNodeIds from the full list
                const ids: Record<string, boolean> = {}
                for (const group of action.payload.sessions) {
                    for (const node of group.saved_nodes) {
                        ids[node.node_id] = true
                    }
                }
                state.savedNodeIds = ids
            })
            .addCase(fetchSavedNodes.rejected, (state) => {
                state.loading = false
            })
            // saveNode — optimistic add
            .addCase(saveNode.pending, (state, action) => {
                state.savedNodeIds[action.meta.arg] = true
            })
            .addCase(saveNode.rejected, (state, action) => {
                // Rollback on failure
                delete state.savedNodeIds[action.meta.arg]
            })
            .addCase(saveNode.fulfilled, (state, action) => {
                // Ensure the ID is set (in case pending was somehow missed)
                state.savedNodeIds[action.payload.node_id] = true
                // Invalidate list cache so next tab open re-fetches
                state.listLoaded = false
            })
            // unsaveNode — optimistic remove
            .addCase(unsaveNode.pending, (state, action) => {
                delete state.savedNodeIds[action.meta.arg]
            })
            .addCase(unsaveNode.rejected, (state, action) => {
                // Rollback on failure
                state.savedNodeIds[action.meta.arg] = true
            })
            .addCase(unsaveNode.fulfilled, (state, action) => {
                delete state.savedNodeIds[action.payload]
                // Remove from session groups
                for (const group of state.sessionGroups) {
                    group.saved_nodes = group.saved_nodes.filter(
                        (n) => n.node_id !== action.payload
                    )
                }
                // Remove empty session groups
                state.sessionGroups = state.sessionGroups.filter(
                    (g) => g.saved_nodes.length > 0
                )
            })
    },
})

export const { removeSavedNodeId, updateSavedSessionTitle } = savedNodesSlice.actions
export default savedNodesSlice.reducer
